import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { useSeo } from '@/hooks/useSeo';

const values = [
  {
    icon: 'ri-focus-3-line',
    title: '只做索引与介绍',
    desc: '我们把精力放在分类、标签、简介与筛选上，不做播放器，也不做下载引导。',
  },
  {
    icon: 'ri-quill-pen-line',
    title: '内容自己写',
    desc: '简介、影评与资讯由编辑团队整理撰写，尽量在第几集转折处之前停下剧透。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '片单属于你',
    desc: '收藏保存在你的浏览器本地，不需要注册账号，也不会被上传到任何服务器。',
  },
  {
    icon: 'ri-compass-3-line',
    title: '帮你少纠结',
    desc: '多维筛选加编辑推荐，把「今晚看什么」的决策时间压缩到十分钟以内。',
  },
];

const stats = [
  { value: '1,286', label: '收录影视作品' },
  { value: '312', label: '影视资讯文章' },
  { value: '26', label: '细分题材标签' },
  { value: '4', label: '内容频道' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 91大神影视平台',
    description:
      '了解 91大神影视平台的定位、内容原则与联系方式。我们聚合电影、剧集与综艺资源信息，提供多维筛选与影视资讯，不提供播放与下载服务。',
    keywords: '91大神, 关于我们, 影视平台介绍, 影视聚合, 联系方式',
    canonicalPath: '/about',
  });

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-accent-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">关于我们</span>
        </nav>

        <section className="mt-4 overflow-hidden rounded-lg border border-background-200/70">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr]">
            <div className="bg-background-50 p-6 md:p-9">
              <p className="font-label text-xs uppercase tracking-[0.2em] text-accent-600">About Us</p>
              <h1 className="mt-2 font-display text-2xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
                让找一部片，
                <br className="hidden md:block" />
                变得像翻书一样简单
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-7 text-foreground-600">
                91大神影视平台是一个影视内容聚合站点，成立于 2023 年。我们相信，观众真正缺的不是「更多资源」，而是一份可信、清楚、不绕弯子的索引。
              </p>
              <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-600">
                所以这里的每一部作品都有完整的题材标签、地区、年份、简介与主创信息；每一篇资讯都尽量告诉你「值不值得看」，而不只是「它上线了」。
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/category/movie"
                  className="whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
                >
                  开始浏览
                </Link>
                <a
                  href="mailto:hello@91dashen.example"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
                >
                  <i className="ri-mail-line"></i>
                  邮件联系我们
                </a>
              </div>
            </div>
            <div className="relative min-h-[220px] w-full bg-background-200 lg:min-h-full">
              <img
                src="https://readdy.ai/api/search-image?query=Editorial%20photo%20of%20a%20quiet%20film%20screening%20room%20with%20warm%20amber%20lamp%20light%2C%20rows%20of%20empty%20seats%2C%20dark%20navy%20walls%2C%20minimal%20cinematic%20atmosphere&width=900&height=800&seq=about-editorial-2026&orientation=portrait"
                alt="91大神影视平台编辑室"
                title="91大神影视平台 - 内容编辑与放映空间"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-lg border border-background-200/70 bg-background-50 p-4">
              <p className="font-display text-2xl font-extrabold text-foreground-950">{stat.value}</p>
              <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </section>

        <section className="mt-8">
          <h2 className="font-display text-xl font-bold text-foreground-950">我们的内容原则</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200/70 bg-background-100 p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                  <i className={`${item.icon} text-base`}></i>
                </span>
                <h3 className="mt-3 text-sm font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-[1.2fr_1fr]">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h2 className="font-display text-lg font-bold text-foreground-950">联系方式</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              内容纠错、作品推荐、合作咨询或活动投稿，都可以直接发邮件给我们。我们通常会在两个工作日内回复。
            </p>
            <ul className="mt-4 space-y-2.5">
              {[
                { icon: 'ri-mail-line', label: '内容与纠错', value: 'hello@91dashen.example' },
                { icon: 'ri-customer-service-2-line', label: '合作与咨询', value: 'partner@91dashen.example' },
                { icon: 'ri-map-pin-line', label: '办公地址', value: '北京市朝阳区影视产业园 B 座 8 层' },
                { icon: 'ri-time-line', label: '服务时间', value: '周一至周五 10:00 - 18:00' },
              ].map((row) => (
                <li key={row.label} className="flex items-start gap-3 rounded-md bg-background-100 px-3.5 py-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-50 text-accent-700">
                    <i className={`${row.icon} text-sm`}></i>
                  </span>
                  <div>
                    <p className="text-[11px] text-foreground-400">{row.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{row.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col justify-between rounded-lg bg-primary-950 p-5 text-background-100 md:p-6">
            <div>
              <h2 className="font-display text-lg font-bold text-white">版权说明</h2>
              <p className="mt-2 text-sm leading-7 text-background-300">
                本站展示的海报、剧照与文字介绍均来源于公开信息或用于内容说明的示意素材。本站不存储、不转码、不分发任何影视文件，不提供在线播放与下载功能。
              </p>
              <p className="mt-2 text-sm leading-7 text-background-300">
                如你是权利方，认为站内信息需要调整或移除，请通过上方邮箱联系我们，我们会尽快处理。
              </p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/news"
                className="whitespace-nowrap rounded-md bg-accent-500 px-3.5 py-2 text-xs font-bold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                阅读影视资讯
              </Link>
              <Link
                to="/favorites"
                className="whitespace-nowrap rounded-md border border-white/25 px-3.5 py-2 text-xs text-background-100 transition-colors hover:bg-white/10"
              >
                查看我的收藏
              </Link>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}